import { Suspense, lazy, useState } from 'react';
import { useT } from '@/store';
import { AddressSearchInput } from '@/components/ui/AddressSearchInput';
import { parseDecimalInput } from '@/lib/format';

/* The same picker the client portal has used since the order form shipped,
   lazily imported for the same reason: Leaflet is the largest dependency in
   this repo and most opens of a quarry or site dialog are an edit, where the
   pin is already set and nobody needs a map. */
const SitePinPicker = lazy(() =>
  import('@/client/SitePinPicker').then((m) => ({ default: m.SitePinPicker })),
);

/**
 * Latitude and longitude, with the map that already existed.
 *
 * WHY THIS IS HERE. A walkthrough of a real installation day found the
 * quarry and site forms demanding LAT and LNG as bare decimals, with no
 * address field and no map. The import path refuses without them too
 * ("Trūksta privalomų laukų: Platuma, Ilguma"). So a carrier setting up
 * twenty-five sites opened Google Maps, found each place, right-clicked it
 * and copied two numbers — fifty lookups before the product had done
 * anything for him.
 *
 * And `SitePinPicker` was in this repository the whole time, working, wired
 * into the CLIENT's order form so that a client could drop a pin for a
 * delivery — while the carrier who owns the system typed decimals. The same
 * shape as the fuel POST route with no screen (V-32) and the invoice PDF
 * with no button (V-42): the capability was built, and the person who needed
 * it could not reach it.
 *
 * THE SEARCH BOX IS THE SECOND HALF OF THAT SAME FINDING. Dropping a pin
 * beats copying decimals and still means finding the place by eye, on a map
 * that opens over Lithuania rather than over the address in the delivery
 * note. Geocoding was in the repo too by then — /api/geocode/search, self-
 * hosted, one route — reachable only from the order form's one-off delivery
 * field. So the address a carrier already has in writing could be typed for
 * the load nobody records, and not for the quarry he loads from every day.
 * Type it here and the boxes and the pin are both written at once.
 *
 * THE BOXES STAY. Dropping the map in and deleting the fields would break
 * the case they were right for: a carrier who has the coordinates on paper
 * from a surveyor, or who is correcting a pin by a few metres, wants to type
 * them. The map WRITES the boxes and the boxes stay editable, so the two are
 * one value with two ways in.
 *
 * COLLAPSED BY DEFAULT for the same reason it is lazily imported: most opens
 * are an edit of a place whose pin is already right, and a 260-pixel map
 * unfolding over the form every time is a cost paid by everybody to help the
 * first morning. Open it once and it stays open for that dialog.
 */
export function CoordinateField({ lat, lng, onChange, radiusM = 150 }) {
  const t = useT();
  const [open, setOpen] = useState(false);

  const parsed = (() => {
    // V-351. Folded here too, so the pin the map draws is the coordinate
    // the save will actually send. Doing it only in the two submit()s would
    // leave "54,687" showing a correct pin and storing 54 — the harder
    // failure to believe, because the operator watched the map agree.
    const parsedLat = parseDecimalInput(lat);
    const parsedLng = parseDecimalInput(lng);
    return Number.isNaN(parsedLat) || Number.isNaN(parsedLng)
      ? null
      : { lat: parsedLat, lng: parsedLng };
  })();

  return (
    <div className="space-y-2">
      {/* Above the boxes, because it is the way in that needs no
          coordinates: a carrier reads the address off the delivery note,
          types it, and the two boxes below fill themselves. Picking a
          suggestion also UNFOLDS the map — a geocoder returns the address it
          understood, which is not always the one that was meant, and a pin
          taken on trust is how a geofence ends up two streets away. Leaflet
          loads at that point rather than on every open of the dialog, which
          is the same bargain the toggle below already strikes. */}
      <label className="block">
        <span className="mb-1.5 block text-[10.5px] font-bold uppercase
                         tracking-[0.03em] text-dim">
          {t('address.searchLabel')}
        </span>
        <AddressSearchInput
          onSelect={(result) => {
            onChange({ lat: String(result.lat), lng: String(result.lng) });
            setOpen(true);
          }}
        />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1.5 block text-[10.5px] font-bold uppercase
                           tracking-[0.03em] text-dim">
            {t('data.latLabel')}
          </span>
          <input
            className="field w-full"
            value={lat}
            onChange={(e) => onChange({ lat: e.target.value, lng })}
            inputMode="decimal"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-[10.5px] font-bold uppercase
                           tracking-[0.03em] text-dim">
            {t('data.lngLabel')}
          </span>
          <input
            className="field w-full"
            value={lng}
            onChange={(e) => onChange({ lat, lng: e.target.value })}
            inputMode="decimal"
          />
        </label>
      </div>

      <button
        type="button"
        className="btn !px-2.5 !py-1 !text-[11px]"
        onClick={() => setOpen((was) => !was)}
      >
        {t(open ? 'data.hideMap' : 'data.pickOnMap')}
      </button>

      {open ? (
        <>
          <Suspense
            fallback={<div className="h-[260px] animate-pulse rounded-[11px] bg-white/5" />}
          >
            <SitePinPicker
              value={parsed}
              /* Written back as text, to the same precision the picker
                 itself rounds to. The boxes hold strings — a half-typed
                 "54." has to survive in them — so the map writes strings
                 too rather than making the field switch types under the
                 user's hands. */
              onChange={(pin) => onChange({ lat: String(pin.lat), lng: String(pin.lng) })}
              radiusM={radiusM}
              ariaLabel={t('data.mapLabel')}
            />
          </Suspense>
          <p aria-live="polite" className="text-[10.5px] leading-snug text-dim">
            {parsed
              ? t('data.pinSet', { lat: parsed.lat.toFixed(5), lng: parsed.lng.toFixed(5) })
              : t('data.pinPrompt')}
          </p>
        </>
      ) : null}
    </div>
  );
}
